import { memo } from 'react';
import styled from 'styled-components';
import { useDownloadAssetsWithWarning } from '@frameio/assets/operations';
import { useFeatureFlagEnabled } from '@frameio/feature-flags';
import {
  useGetProjectMemberTotalCountQuery,
  useGetProjectPermissionsQuery,
  useProjectSheetHeaderDataFragment,
} from '@frameio/graphql-client/generated';
import { number, plural } from '@frameio/i18n';
import { useMemberAccessFlow } from '@frameio/invites/hooks';
import { useToggleProjectNotifications } from '@frameio/notifications/hooks';
import {
  useActiveAccountId,
  useActiveProjectId,
} from '@frameio/shared/contexts';
import { useGetActiveWorkspaceIdByProjectId } from '@frameio/shared/contexts/ActiveEntitiesContext/ActiveWorkspaceIdContext';
import { useIsMatchingClientName } from '@frameio/shared/contexts/ClientNameContext';
import { useCopyLink } from '@frameio/shared/routing';
import { formatCount } from '@frameio/utils/formatters/formatters';
import {
  Box,
  MenuButton,
  SheetHead,
  Text,
  theme,
  useDeferredMount,
  VisuallyHidden,
} from '@frameio/vapor';
import {
  IconDownload,
  IconLink,
  IconPeople,
  IconPersonCircleAdd,
} from '@frameio/vapor-icons';
import TEXT from './generated/TEXT/ProjectSwitcherSheetHeader';
import { ProjectSwitcherProjectMenu } from './ProjectSwitcherProjectMenu';
import { useProjectSettings } from './ProjectSwitcherProjectSettings';

TEXT.DEFINE(
  {
    /** Label for the project member count shown in the project switcher sheet header */
    memberCount: (count: number) => {
      return plural(count, {
        one: '1 Member',
        other: `${number(count, 'compact-short')} Members`,
      });
    },
    /** Context menu action label for copying the current project's URL */
    copyLink: `Copy Project URL`,
    /** Context menu action label for downloading all assets in the project */
    downloadAllFiles: 'Download All Assets',
    /** Label for the invite/members access control button in the project switcher header */
    inviteLabel: 'Invite to Project',
    /** Menu item label for inviting people to the current project */
    inviteMembers: 'Invite Members',
    /** Menu item label for managing existing project members */
    manageMembers: 'Manage Members',
  },
  { formatCount }
);

export const ProjectSwitcherSheetHeader = memo(() => {
  const accountId = useActiveAccountId();
  const projectId = useActiveProjectId();
  const { workspaceId } = useGetActiveWorkspaceIdByProjectId(projectId);
  const { clickProjectSettings } = useProjectSettings();
  const isWebClient = useIsMatchingClientName('web');

  const { data: projectData } = useGetProjectMemberTotalCountQuery({
    variables: { projectId },
    skip: !projectId,
    fetchPolicy: 'cache-and-network',
  });

  const project = useProjectSheetHeaderDataFragment({
    from: {
      __typename: 'Project',
      id: projectId,
    },
  });

  const rootFolderId = project?.data?.rootAssetId;
  const hasNotificationsEnabled = project?.data?.notificationsEnabled;

  const { toggleProjectNotificationsMutation: toggleProjectNotifications } =
    useToggleProjectNotifications({
      projectId,
    });

  const { data: projectPermissionsData } = useGetProjectPermissionsQuery({
    variables: { projectId },
    skip: !projectId,
  });

  const canUpdate =
    projectPermissionsData?.account?.project?.permissions?.canUpdate;
  const canDownload =
    projectPermissionsData?.account?.project?.permissions?.canDownloadAsset;
  const canEditUserPermissions =
    projectPermissionsData?.account?.project?.permissions
      ?.canEditUserPermissions;

  const onClickSettings = () => {
    if (projectId && workspaceId) {
      clickProjectSettings(projectId, workspaceId);
    }
  };

  const copyLink = useCopyLink();
  const { downloadAssets } = useDownloadAssetsWithWarning();

  const handleCopyLink = () => {
    copyLink({
      page: 'folder',
      projectId,
      folderAssetId: undefined, // undefined for root folder
    });
  };

  const hideSuggestedMembers = useFeatureFlagEnabled(
    'v4.invites.hide_suggested_members'
  );

  const { renderMemberAccessButton, renderedMemberAccessDialog } =
    useMemberAccessFlow({
      accountId,
      hideSuggestedMembers,
      isDialogShowingInitially: false,
      label: TEXT.inviteLabel,
      resourceId: projectId,
      trackingSource: 'ProjectBreadcrumbs',
    });

  const shouldRenderMenuContent = useDeferredMount();

  const title = projectData ? (
    <SheetLabel>
      <TextContainer>
        <VisuallyHidden>
          <Text fontWeight="medium" truncate={true} variant="link">
            {projectData.account?.project?.name}
          </Text>
        </VisuallyHidden>
      </TextContainer>
    </SheetLabel>
  ) : null;

  return (
    <>
      <SheetHead
        contextMenuContent={
          <>
            <Box paddingBottom={0} paddingTop={8}>
              <MenuButton iconBefore={<IconLink />} onPress={handleCopyLink}>
                {TEXT.copyLink}
              </MenuButton>
              {canDownload && rootFolderId && (
                <MenuButton
                  iconBefore={<IconDownload />}
                  onPress={() =>
                    downloadAssets([rootFolderId], {
                      trackDownloadingState: false,
                    })
                  }
                >
                  {TEXT.downloadAllFiles}
                </MenuButton>
              )}
            </Box>
            {canEditUserPermissions &&
              isWebClient &&
              renderMemberAccessButton(({ onClick, ...bind }) => (
                <>
                  <MenuButton
                    {...bind}
                    iconBefore={<IconPersonCircleAdd />}
                    onPress={() => onClick?.('invites')}
                  >
                    {TEXT.inviteMembers}
                  </MenuButton>
                  <MenuButton
                    {...bind}
                    iconBefore={<IconPeople />}
                    onPress={() => onClick?.('members')}
                  >
                    {TEXT.manageMembers}
                  </MenuButton>
                </>
              ))}
            {typeof hasNotificationsEnabled === 'boolean' && (
              <ProjectSwitcherProjectMenu
                hasNotificationsEnabled={hasNotificationsEnabled}
                onSelectNotifications={toggleProjectNotifications}
                {...(canUpdate ? { onSettingsClick: onClickSettings } : {})}
              />
            )}
          </>
        }
        title={title}
      />
      {shouldRenderMenuContent ? <>{renderedMemberAccessDialog}</> : null}
    </>
  );
});

const SheetLabel = styled.div`
  align-items: center;
  display: flex;
  padding-left: calc(${theme.spaces[32]} + ${theme.spaces[4]});
`;

const TextContainer = styled.div`
  margin-top: ${theme.spaces[2]};
  padding-top: ${theme.spaces[32]};
`;
